import { CalendarDays, CalendarRange, ChevronDown, RotateCcw } from 'lucide-react';
import { Popover as PopoverPrimitive } from 'radix-ui';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { Button } from '../primitives/button.js';
import { Calendar, parseLocalDay, toLocalDay } from '../primitives/calendar.js';
import { formatLocalDay } from '../primitives/date-picker.js';
import { controlBase } from '../primitives/input.js';

export interface FilterBarProps {
  children: React.ReactNode;
  /** Number of non-default filters; shows the reset action when > 0. */
  activeCount?: number;
  onReset?: () => void;
  /** Right-aligned actions (e.g. export). */
  actions?: React.ReactNode;
  className?: string;
  label?: string;
}

export function FilterBar({ children, activeCount = 0, onReset, actions, className, label = 'Filtros' }: FilterBarProps) {
  return (
    <div role="group" aria-label={label} className={cn('mb-6 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between', className)}>
      <div className="flex min-w-0 flex-1 flex-wrap items-end gap-3 [&>*]:min-w-[min(100%,180px)]">
        {children}
        {activeCount > 0 && onReset ? (
          <Button variant="ghost" size="md" onClick={onReset} leadingIcon={<RotateCcw aria-hidden />}>
            Limpiar filtros ({activeCount})
          </Button>
        ) : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/** Label + control stack used inside a FilterBar. */
export function FilterItem({ label, children, htmlFor }: { label: string; children: React.ReactNode; htmlFor?: string }) {
  return (
    <div className="grid gap-1">
      <label htmlFor={htmlFor} className="text-helper font-medium text-text-secondary">
        {label}
      </label>
      {children}
    </div>
  );
}

export interface DateRange {
  /** Local date YYYY-MM-DD (inclusive). */
  from: string;
  /** Local date YYYY-MM-DD (inclusive). */
  to: string;
}

export interface DateRangePreset {
  id: string;
  label: string;
  range: DateRange;
}

function shift(day: string, days: number): string {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Standard presets relative to `today` (a local YYYY-MM-DD in the business timezone). */
export function defaultDatePresets(today: string): DateRangePreset[] {
  const monthStart = `${today.slice(0, 8)}01`;
  return [
    { id: '7d', label: 'Últimos 7 días', range: { from: shift(today, -6), to: today } },
    { id: '30d', label: 'Últimos 30 días', range: { from: shift(today, -29), to: today } },
    { id: '90d', label: 'Últimos 90 días', range: { from: shift(today, -89), to: today } },
    { id: 'month', label: 'Este mes', range: { from: monthStart, to: today } },
  ];
}

export interface DateRangePickerProps {
  value: DateRange;
  onChange: (value: DateRange) => void;
  /** Local "today" in the business timezone, used for presets and max. */
  today: string;
  presets?: DateRangePreset[];
  /** Timezone name displayed to users (dates are interpreted in it). */
  timeZoneLabel?: string;
  disabled?: boolean;
  className?: string;
}

export function DateRangePicker({ value, onChange, today, presets, timeZoneLabel, disabled, className }: DateRangePickerProps) {
  const id = React.useId();
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState<{ from?: Date; to?: Date } | undefined>();
  const list = presets ?? defaultDatePresets(today);
  const invalid = value.from > value.to;
  const activePreset = list.find((p) => p.range.from === value.from && p.range.to === value.to);
  const draftRange = draft?.from && draft.to ? { from: toLocalDay(draft.from), to: toLocalDay(draft.to) } : undefined;

  // After a complete range, the next click starts a new range instead of nudging the nearest end.
  const selectDay = (r: { from?: Date; to?: Date } | undefined, day: Date) => {
    if (draft?.from && draft.to) setDraft({ from: day, to: undefined });
    else setDraft(r ? { from: r.from, to: r.to } : undefined);
  };
  const openChange = (next: boolean) => {
    setOpen(next);
    if (next) setDraft({ from: parseLocalDay(value.from), to: parseLocalDay(value.to) });
  };
  const apply = (range: DateRange) => {
    onChange(range);
    setOpen(false);
  };

  return (
    <div className={cn('grid gap-1', className)}>
      <span id={`${id}-label`} className="flex items-center gap-1.5 text-helper font-medium text-text-secondary">
        <CalendarRange aria-hidden className="size-4" />
        Periodo{timeZoneLabel ? <span className="font-normal"> · {timeZoneLabel}</span> : null}
      </span>
      <PopoverPrimitive.Root open={open} onOpenChange={openChange}>
        <PopoverPrimitive.Trigger
          disabled={disabled}
          aria-labelledby={`${id}-label ${id}-value`}
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? `${id}-err` : undefined}
          className={cn(controlBase, 'flex w-auto min-w-[min(100%,280px)] items-center gap-2 text-left')}
        >
          <CalendarDays aria-hidden className="size-[18px] shrink-0 text-text-secondary" />
          <span id={`${id}-value`} className="cm-tabular truncate">
            {activePreset ? `${activePreset.label} · ` : ''}
            {formatLocalDay(value.from)} – {formatLocalDay(value.to)}
          </span>
          <ChevronDown aria-hidden className="ml-auto size-[18px] shrink-0 text-text-muted" />
        </PopoverPrimitive.Trigger>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            align="start"
            sideOffset={6}
            collisionPadding={16}
            className="z-50 max-w-[calc(100vw-32px)] rounded-menu border border-border-subtle bg-surface-default shadow-overlay animate-fade-in"
          >
            <div className="flex flex-col sm:flex-row">
              <div role="group" aria-label="Periodos predefinidos" className="flex flex-wrap gap-1 border-b border-border-subtle p-2 sm:w-44 sm:flex-col sm:flex-nowrap sm:border-b-0 sm:border-r">
                {list.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    aria-pressed={activePreset?.id === p.id}
                    onClick={() => apply(p.range)}
                    className={cn(
                      'rounded-control px-3 py-2 text-left text-body text-text-primary hover:bg-surface-subtle',
                      'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-focus',
                      activePreset?.id === p.id && 'bg-brand-tint font-medium text-brand-primary hover:bg-brand-tint',
                    )}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <div>
                <Calendar
                  mode="range"
                  showOutsideDays={false}
                  numberOfMonths={2}
                  className="hidden md:block"
                  selected={draft?.from ? { from: draft.from, to: draft.to } : undefined}
                  onSelect={selectDay}
                  defaultMonth={parseLocalDay(shiftMonthBack(value.to))}
                  disabled={{ after: parseLocalDay(today) }}
                />
                <Calendar
                  mode="range"
                  showOutsideDays={false}
                  numberOfMonths={1}
                  className="md:hidden"
                  selected={draft?.from ? { from: draft.from, to: draft.to } : undefined}
                  onSelect={selectDay}
                  defaultMonth={parseLocalDay(value.to)}
                  disabled={{ after: parseLocalDay(today) }}
                />
                <div className="flex items-center justify-between gap-3 border-t border-border-subtle px-3 py-2">
                  <span className="cm-tabular text-helper text-text-secondary">
                    {draftRange ? `${formatLocalDay(draftRange.from)} – ${formatLocalDay(draftRange.to)}` : 'Elige fecha inicial y final'}
                  </span>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
                      Cancelar
                    </Button>
                    <Button size="sm" disabled={!draftRange} onClick={() => draftRange && apply(draftRange)}>
                      Aplicar
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
      {invalid ? (
        <p id={`${id}-err`} role="alert" className="text-helper font-medium text-danger">
          La fecha inicial debe ser anterior a la final.
        </p>
      ) : null}
    </div>
  );
}

/** First day of the month before `day`, so a two-month calendar ends on the selected end month. */
function shiftMonthBack(day: string): string {
  const [y, m] = day.split('-').map(Number);
  const d = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 2, 1));
  return d.toISOString().slice(0, 10);
}
